<template>
  <div class="pb-3">
    <va-card :title="$t(`sena.cards.${titleCard}`)">
      <ValidationObserver v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(onSubmit)">
          <div class="row">
            <div class="flex md12">

              <ValidationProvider rules="required|numeric" name="Número" v-slot="{ errors }">
                <va-input
                  v-model="edition.edition_number"
                  :label="$t('sena.forms.numberLabel')"
                  :placeholder="$t('sena.forms.numberPlaceholder')"
                  :success="errors.length === 0 && String(edition.edition_number).length > 0"
                  :error="errors.length > 0"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider rules="required" name="Nome" v-slot="{ errors }">
                <va-input
                  v-model="edition.name"
                  :label="$t('sena.forms.nameLabel')"
                  :placeholder="$t('sena.forms.namePlaceholder')"
                  :success="errors.length === 0 && edition.name.length > 0"
                  :error="errors.length > 0"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider rules="required" name="Prêmio" v-slot="{ errors }">
                <va-input
                  v-model="edition.prize_amount"
                  :label="$t('sena.forms.prizeLabel')"
                  :placeholder="$t('sena.forms.prizePlaceholder')"
                  :disabled="prizeFrozen"
                  :success="errors.length === 0 && Number(edition.prize_amount) > 0"
                  :error="errors.length > 0"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider rules="required" name="Prêmio de 5 acertos" v-slot="{ errors }">
                <va-input
                  v-model="edition.prize_amount_5"
                  :label="$t('sena.forms.prize5Label')"
                  :placeholder="$t('sena.forms.prize5Placeholder')"
                  :disabled="prizeFrozen"
                  :success="errors.length === 0 && Number(edition.prize_amount_5) > 0"
                  :error="errors.length > 0"
                  :error-messages="errors"
                />
              </ValidationProvider>
              <p v-if="prizeFrozen" class="sena-hint">
                {{ $t('sena.hints.prizeFrozen') }}
              </p>
              <p class="sena-hint">
                {{ $t('sena.hints.rulesInSettings') }}
              </p>

              <!--
                lockout_at é DERIVADO por fn_sena_recalc_lockout a partir do
                jogo mais cedo da edição. Somente leitura de propósito: editar
                à mão seria sobrescrito na próxima mexida nos jogos.
              -->
              <div v-if="isUpdate" class="sena-readonly mt-3">
                <p>
                  <b>{{ $t('sena.forms.lockoutLabel') }}:</b>
                  {{ lockoutText }}
                </p>
                <p class="sena-hint">{{ $t('sena.hints.lockoutDerived') }}</p>
              </div>

              <div class="d-flex mt-3">
                <va-button
                  type="submit"
                  :disabled="buttonSubmitDisabled"
                  color="success"
                  class="my-0">
                  {{ textSubmit }}
                </va-button>
                <va-button
                  type="button"
                  class="my-0"
                  color="danger"
                  @click="cancel">
                  {{ $t("forms.cancel") }}
                </va-button>
              </div>
            </div>
          </div>
        </form>
      </ValidationObserver>

      <!-- Transições de status. Passam por fn_sena_set_edition_status para
           que o actor_id do admin caia na auditoria; um update_sena_edition
           direto gravaria actor_id = NULL. -->
      <div v-if="isUpdate" class="sena-transitions mt-4">
        <hr/>
        <h6 class="mt-3">{{ $t('sena.cards.transitions') }}</h6>

        <div class="d-flex mt-2">
          <va-button
            v-if="edition.status === 'DRAFT'"
            color="success"
            :disabled="transitionBusy"
            @click="setStatus('OPEN')">
            {{ $t('sena.buttons.open') }}
          </va-button>

          <va-button
            v-if="edition.status === 'OPEN'"
            color="warning"
            :disabled="transitionBusy"
            @click="setStatus('LOCKED')">
            {{ $t('sena.buttons.lock') }}
          </va-button>

          <va-button
            v-if="['DRAFT','OPEN','LOCKED'].includes(edition.status)"
            color="danger"
            :disabled="transitionBusy"
            @click="confirmCancel = true">
            {{ $t('sena.buttons.cancelEdition') }}
          </va-button>
        </div>

        <p class="sena-hint mt-2" v-if="edition.status === 'DRAFT'">
          {{ $t('sena.hints.openRequires') }}
        </p>
        <p class="sena-hint mt-2" v-if="edition.status === 'OPEN'">
          {{ $t('sena.hints.lockOptional') }}
        </p>
      </div>
    </va-card>

    <va-modal
      v-model="confirmCancel"
      size="small"
      :title="$t('sena.modals.cancelTitle')"
      :message="$t('sena.modals.cancelMessage')"
      :okText="$t('modals.confirm')"
      :cancelText="$t('modals.cancel')"
      @ok="setStatus('CANCELED')"
    />
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { senaErrorMixin } from './senaErrors'

const SET_STATUS = gql`
  mutation SetStatus($id: Int!, $status: String!) {
    fn_sena_set_edition_status(args: {p_edition_id: $id, p_status: $status}) {
      id
      status
      lockout_at
    }
  }
`

export default {
  name: 'SenaEditionAddUpdate',
  mixins: [senaErrorMixin],
  props: {
    page: {
      type: Number,
      required: true,
    },
    titleCard: {
      type: String,
      required: true,
    },
    updateData: {
      required: true,
    },
  },
  data () {
    return {
      edition: {
        edition_number: '',
        name: '',
        prize_amount: '25000.00',
        prize_amount_5: '1000.00',
        status: 'DRAFT',
        lockout_at: null,
      },
      buttonSubmitDisabled: false,
      transitionBusy: false,
      confirmCancel: false,
      textSubmit: this.$t('forms.submit'),
    }
  },
  created () {
    if (this.updateData !== null) {
      this.edition_get(this.updateData)
    }
  },
  watch: {
    updateData (value) {
      if (value !== null) this.edition_get(value)
    },
  },
  computed: {
    isUpdate () {
      return this.updateData !== null
    },
    // trg_sena_edition_guard levanta SENA_PRIZE_FROZEN fora de DRAFT.
    // Desabilitar o campo evita o erro em vez de explicar depois.
    prizeFrozen () {
      return this.isUpdate && this.edition.status !== 'DRAFT'
    },
    lockoutText () {
      return this.edition.lockout_at
        ? this.$moment(this.edition.lockout_at).format('DD/MM/YYYY [às] HH:mm')
        : this.$t('sena.labels.lockoutPending')
    },
  },
  methods: {
    cancel () {
      this.$emit('showAdd', false)
    },
    edition_get (data) {
      Object.keys(data).forEach(key => {
        if (key !== '__typename' && key !== 'progress') {
          this.$set(this.edition, key, data[key] !== null ? data[key] : '')
        }
      })
    },
    setStatus (status) {
      this.transitionBusy = true
      this.$apollo
        .mutate({
          mutation: SET_STATUS,
          variables: { id: this.edition.id, status },
        })
        .then(({ data }) => {
          this.transitionBusy = false
          const row = data.fn_sena_set_edition_status[0]
          this.edition.status = row.status
          this.edition.lockout_at = row.lockout_at
          this.senaToastSuccess(`sena.toasts.status_${status}`)
          this.$emit('showMore', this.page)
        })
        .catch(error => {
          this.transitionBusy = false
          this.senaToastError(error, 'toasts.errorUpdate')
        })
    },
    onSubmit () {
      this.buttonSubmitDisabled = true
      this.textSubmit = this.$t('forms.submitting')
      const self = this
      const {
        edition_number: editionNumber, name,
        prize_amount: prizeAmount, prize_amount_5: prizeAmount5,
      } = this.edition

      // Fora de DRAFT os DOIS prêmios são congelados por trigger
      // (SENA_PRIZE_FROZEN): nem enviamos os campos.
      const setUpdate = this.prizeFrozen
        ? '{name: $name, edition_number: $editionNumber}'
        : '{name: $name, edition_number: $editionNumber, prize_amount: $prizeAmount, prize_amount_5: $prizeAmount5}'

      // As declarações acompanham o _set: variável declarada e não usada é
      // erro de validação do GraphQL, não sobra inofensiva.
      const varsUpdate = this.prizeFrozen
        ? '$id: Int!, $editionNumber: Int!, $name: String!'
        : '$id: Int!, $editionNumber: Int!, $name: String!, $prizeAmount: numeric!, $prizeAmount5: numeric!'

      this.$apollo
        .mutate({
          mutation: this.isUpdate ? gql`
            mutation MyMutation(${varsUpdate}) {
              update_sena_edition_by_pk(pk_columns: {id: $id}, _set: ${setUpdate}) {
                id
              }
            }
          ` : gql`
            mutation MyMutation($editionNumber: Int!, $name: String!, $prizeAmount: numeric!, $prizeAmount5: numeric!) {
              insert_sena_edition_one(object: {
                edition_number: $editionNumber,
                name: $name,
                prize_amount: $prizeAmount,
                prize_amount_5: $prizeAmount5
              }) {
                id
              }
            }
          `,
          variables: this.isUpdate ? {
            id: this.edition.id,
            editionNumber: Number(editionNumber),
            name,
            ...(this.prizeFrozen ? {} : {
              prizeAmount: String(prizeAmount),
              prizeAmount5: String(prizeAmount5),
            }),
          } : {
            editionNumber: Number(editionNumber),
            name,
            prizeAmount: String(prizeAmount),
            prizeAmount5: String(prizeAmount5),
          },
        })
        .then(() => {
          self.buttonSubmitDisabled = false
          self.textSubmit = self.$t('forms.submit')
          self.$emit('showAdd', false)
          self.$emit('showMore', self.page)
          self.senaToastSuccess(self.isUpdate ? 'toasts.successUpdate' : 'toasts.successCreate')
        })
        .catch(error => {
          self.buttonSubmitDisabled = false
          self.textSubmit = self.$t('forms.submit')
          self.senaToastError(error, self.isUpdate ? 'toasts.errorUpdate' : 'toasts.errorCreate')
        })
    },
  },
}
</script>

<style lang="scss">
.va-message-list {
  &__message {
    vertical-align: middle;
    font-size: $font-size-mini;
    color: rgb(248, 112, 109);
  }
}

.sena-hint {
  font-size: $font-size-mini;
  color: #888888;
  margin: 0.25rem 0 0;
}

.sena-readonly {
  background: rgba(0, 0, 0, 0.03);
  padding: 0.75rem 1rem;
  border-radius: 4px;
}
</style>
